import styled from '@emotion/styled';
import { colors, fontFamilies } from '../baseStyle/Variables';
import { ReactComponent as InfoIcon } from '../../assets/info_icon.svg';
import ReactTooltip, { Place } from 'react-tooltip';
import React, { useCallback, useRef, useState } from 'react';

const SVGContainer = styled.button<{ inline?: boolean; focused?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: ${(props) => (props.inline ? 'relative' : 'absolute')};
  display: inline-flex;
  direction: column;
  align-items: center;
  justify-content: center;
  height: ${(props) => (props.inline ? 'auto' : '2rem')};
  padding-left: ${(props) => (props.inline ? '.1rem' : '0')};
  top: ${(props) => (props.inline ? '2px' : '0')};
  right: 0;
  svg {
    path {
      fill: ${(props) => (props.focused ? colors.koalafiBlue : colors.middleGrey)};
    }
  }
`;

const StyledReactTooltip = styled(ReactTooltip)<{ maxWidth?: string }>`
  max-width: ${(props) => props.maxWidth || '225px'};
  box-sizing: border-box;
  background-color: ${colors.white} !important;
  box-shadow: 0px 2px 6px ${colors.darkGray} !important;
  opacity: 1 !important;
  font-family: ${fontFamilies.interLight};
  padding: 0.8rem !important;
  &:after {
    border-top-color: ${colors.white} !important;
  }
`;

const Tooltip: React.FC<{
  id: string;
  tooltipContent: React.ReactElement;
  focused?: boolean;
  size?: string;
  inline?: boolean;
  place?: Place;
  maxWidth?: string;
}> = ({ id, tooltipContent, focused, size, inline, place, maxWidth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const showTooltip = useCallback(() => {
    if (triggerRef.current) {
      ReactTooltip.show(triggerRef.current);
      setIsOpen(true);
    }
  }, []);

  const hideTooltip = useCallback(() => {
    if (triggerRef.current) {
      ReactTooltip.hide(triggerRef.current);
      setIsOpen(false);
    }
  }, []);

  const toggleTooltip = useCallback(() => {
    if (isOpen) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [isOpen, showTooltip, hideTooltip]);

  return (
    <SVGContainer
      ref={triggerRef}
      className="info-icon-tooltip"
      inline={inline}
      focused={focused}
      data-tip="tooltip"
      data-for={`${id}-tooltip`}
      aria-controls={`${id}-tooltip`}
      aria-label={`More info about ${id}`}
      aria-expanded={isOpen}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          hideTooltip();
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleTooltip();
        }
      }}
    >
      <InfoIcon aria-hidden="true" height={size || '15px'} width={size || '15px'} />
      <StyledReactTooltip
        className="tooltip"
        id={`${id}-tooltip`}
        effect="solid"
        event="hover"
        type="light"
        globalEventOff="click"
        textColor={colors.darkBlue}
        place={place || 'top'}
        data-tooltip-offset={400}
        role="tooltip"
        maxWidth={maxWidth}
      >
        {tooltipContent}
      </StyledReactTooltip>
    </SVGContainer>
  );
};

export default Tooltip;
